// Admin v2 — Nyereményjáték-sorsolás: vizuális, 3 körös húzás az érvényes nevezők közül (Firestore `subscribers`).
// Sorrend: 3. → 2. → 1. díj, előre kisorsolt pótnyertes nincs; ha a kihúzott nevező nem felel meg,
// az adott díjra új húzás jön az érvényes nevezők közül. Az éles sorsolás minden lépése a `giveaway_draws` kollekcióba kerül
// (nevezői kör pillanatképe + SHA-256 ujjlenyomat, véletlenszám, időpont, döntés), és jegyzőkönyvként nyomtatható.
// E-mailt ez az oldal nem küld: a nyertesek értesítése külön, kézzel történik.
// Élő közvetítéshez a pörgető csak nevezési sorszámokat mutat; név csak a kihúzott nevezőnél jelenik meg.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { addDoc, collection, doc, onSnapshot, serverTimestamp, updateDoc, type DocumentData } from 'firebase/firestore';
import { auth, db } from '../../firebase';
import { fmtInt, toDate } from '../data';
import { Icon } from '../icons';
import logo from '../dq-logo.svg';
import { Badge, Button, Card, ConfirmModal, Field, Modal, Notice, PageHeader, SectionTitle, Segmented, Select, Spinner, StatCard, TextArea, useToast } from '../ui';

/* ---------- díjak (a weboldal nyereményjáték-szövege alapján) ---------- */
const PRIZES = [
  { round: 1, label: '1. díj', title: 'Winmau Blade 6 Triple Core PDC darts tábla', extra: '+ 60 000 Ft értékű Katakomba Darts Shop vásárlási utalvány' },
  { round: 2, label: '2. díj', title: '55 000 Ft értékű', extra: 'Katakomba Darts Shop vásárlási utalvány' },
  { round: 3, label: '3. díj', title: '45 000 Ft értékű', extra: 'Katakomba Darts Shop vásárlási utalvány' },
] as const;

const REASONS = [
  { value: 'not_confirmed', label: 'Az e-mail-cím nincs megerősítve' },
  { value: 'late', label: 'Határidő után nevezett vagy erősített meg' },
  { value: 'not_accepted', label: 'Nem fogadta el a szabályzatot' },
  { value: 'underage', label: 'A 18. életév betöltése nem igazolt' },
  { value: 'no_reply', label: 'Határidőn belül nem válaszolt' },
  { value: 'declined', label: 'Visszautasította a nyereményt' },
  { value: 'unsubscribed', label: 'Leiratkozott az indulási értesítőről' },
  { value: 'other', label: 'Egyéb ok' },
] as const;
type Reason = typeof REASONS[number]['value'];
const reasonLabel = (r?: string) => REASONS.find((x) => x.value === r)?.label ?? r ?? '';

/* ---------- típusok ---------- */
interface Entrant { no: number; id: string; name: string; email: string; enteredAt: Date | null }
type DrawStatus = 'pending' | 'winner' | 'rejected' | 'revoked';
interface Draw {
  round: number; at: string; rand: number; index: number; poolSize: number;
  id: string; no: number; name: string; status: DrawStatus; reason?: string; note?: string; decidedAt?: string;
  checks?: Check[];
}
interface Check { key: Reason; label: string; ok: boolean }
interface Session {
  id: string; test: boolean; createdAt: Date | null; createdBy: string;
  pool: string[]; poolHash: string; draws: Draw[]; finishedAt: Date | null;
}

const ROW = 76; // a pörgető egy sorának magassága (px)
const VISIBLE = 5; // látható sorok (a középső a kihúzott)
const SPIN_MS = 5600;

/* ---------- segédek ---------- */
const maskEmail = (e: string) => {
  const [u, d] = e.split('@');
  return d ? `${u.slice(0, 1)}${'•'.repeat(Math.max(2, Math.min(6, u.length - 1)))}@${d}` : e;
};
const dt = (d: Date | string | null | undefined) => {
  const x = typeof d === 'string' ? new Date(d) : d;
  return x ? x.toLocaleString('hu-HU', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' }) : '—';
};

/** Egyenletes eloszlású véletlen index a böngésző kriptográfiai generátorával (elutasításos mintavétel, torzítás nélkül). */
function fairIndex(n: number): { index: number; rand: number } {
  const max = 0x100000000;
  const limit = max - (max % n);
  const buf = new Uint32Array(1);
  for (;;) {
    crypto.getRandomValues(buf);
    if (buf[0] < limit) return { index: buf[0] % n, rand: buf[0] };
  }
}

async function sha256(text: string): Promise<string> {
  const h = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(text));
  return Array.from(new Uint8Array(h)).map((b) => b.toString(16).padStart(2, '0')).join('');
}

const isEligible = (v: Record<string, unknown>) =>
  Boolean(v.giveawayAccepted) && v.giveawayValid === true && !v.giveawayLate && Boolean(v.confirmed || v.giveawayConfirmedAt);

/** A rendszer által ellenőrizhető feltételek a kihúzás pillanatában, a nevező aktuális adatai alapján.
 *  (A 18. életév betöltését a nevező az űrlapon nyilatkozta; igazolni a nyertes értesítése után kell.) */
function checkEntry(v: Record<string, unknown> | undefined): Check[] {
  return [
    { key: 'not_accepted', label: 'Elfogadta a szabályzatot, 18+ nyilatkozattal', ok: Boolean(v?.giveawayAccepted) },
    { key: 'late', label: 'Határidőn belül nevezett és erősített meg', ok: v?.giveawayValid === true && !v?.giveawayLate },
    { key: 'not_confirmed', label: 'E-mail-cím megerősítve', ok: Boolean(v?.confirmed || v?.giveawayConfirmedAt) },
    { key: 'unsubscribed', label: 'Most is feliratkozó az indulási értesítőn', ok: Boolean(v) },
  ];
}

const sessionFromDoc = (id: string, v: Record<string, unknown>): Session => ({
  id,
  test: Boolean(v.test),
  createdAt: toDate(v.createdAt),
  createdBy: String(v.createdBy ?? ''),
  pool: Array.isArray(v.pool) ? (v.pool as string[]) : [],
  poolHash: String(v.poolHash ?? ''),
  draws: Array.isArray(v.draws) ? (v.draws as Draw[]) : [],
  finishedAt: toDate(v.finishedAt),
});

/** Egy díj állapota: van-e elfogadott nyertese, vagy éppen döntésre váró húzása. */
function roundState(draws: Draw[], round: number): { winner?: Draw; pending?: Draw; tries: number } {
  const ds = draws.filter((d) => d.round === round);
  return { winner: ds.find((d) => d.status === 'winner'), pending: ds.find((d) => d.status === 'pending'), tries: ds.length };
}

/* ---------- konfetti (csak CSS) ---------- */
function Confetti() {
  const bits = useMemo(() => Array.from({ length: 70 }, (_, i) => ({
    left: Math.random() * 100,
    delay: Math.random() * 0.8,
    dur: 2.4 + Math.random() * 1.8,
    size: 6 + Math.random() * 7,
    color: ['#00AFF2', '#d9a514', '#a855f7', '#2bc48a', '#ffffff'][i % 5],
    rot: Math.random() * 360,
  })), []);
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden">
      {bits.map((b, i) => (
        <span
          key={i}
          className="gd-confetti absolute top-[-20px] rounded-[2px]"
          style={{ left: `${b.left}%`, width: b.size, height: b.size * 0.45, background: b.color, animationDelay: `${b.delay}s`, animationDuration: `${b.dur}s`, transform: `rotate(${b.rot}deg)` }}
        />
      ))}
    </div>
  );
}

/* ---------- pörgető ---------- */
function Reel({ strip, target, spinning, digits, onDone }: { strip: number[]; target: number; spinning: boolean; digits: number; onDone: () => void }) {
  const [y, setY] = useState(0);
  const [anim, setAnim] = useState(false);
  useEffect(() => {
    if (!spinning) { setAnim(false); setY(-(target - Math.floor(VISIBLE / 2)) * ROW); return; }
    setAnim(false);
    setY(0);
    const r = requestAnimationFrame(() => requestAnimationFrame(() => { setAnim(true); setY(-(target - Math.floor(VISIBLE / 2)) * ROW); }));
    return () => cancelAnimationFrame(r);
  }, [spinning, target, strip]);
  const fmt = (n: number) => (n > 0 ? `#${String(n).padStart(digits, '0')}` : '');
  return (
    <div className="relative mx-auto w-full max-w-[420px] overflow-hidden rounded-2xl border border-[#31383D] bg-[#0b0f14]" style={{ height: ROW * VISIBLE }}>
      <div
        className="absolute inset-x-0 top-0"
        style={{ transform: `translateY(${y}px)`, transition: anim ? `transform ${SPIN_MS}ms cubic-bezier(0.1, 0.65, 0.12, 1)` : 'none' }}
        onTransitionEnd={() => { if (anim) onDone(); }}
      >
        {strip.map((n, i) => (
          <div key={i} className="flex items-center justify-center font-extrabold italic tabular-nums text-white" style={{ height: ROW, fontSize: 44, letterSpacing: '0.04em' }}>
            {fmt(n)}
          </div>
        ))}
      </div>
      {/* középső kiemelés és elhalványuló szélek */}
      <div className="pointer-events-none absolute inset-x-3 rounded-xl border-2 border-[#00AFF2] shadow-[0_0_32px_rgba(0,175,242,0.35)]" style={{ top: ROW * 2, height: ROW }} />
      <div className="pointer-events-none absolute inset-x-0 top-0 h-[120px] bg-gradient-to-b from-[#0b0f14] to-transparent" />
      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-[120px] bg-gradient-to-t from-[#0b0f14] to-transparent" />
    </div>
  );
}

/* ---------- jegyzőkönyv ---------- */
function printProtocol(s: Session, entrants: Map<string, Entrant>) {
  const esc = (x: string) => x.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c] as string));
  const statusHu: Record<DrawStatus, string> = { pending: 'Döntésre vár', winner: 'Nyertes', rejected: 'Nem felelt meg – új húzás', revoked: 'Utólag visszavonva – új húzás' };
  const rows = s.draws.map((d, i) => `<tr><td>${i + 1}.</td><td>${d.round}. díj</td><td>${esc(dt(d.at))}</td><td>${d.poolSize}</td><td>${d.rand}</td><td>${d.index}</td>
    <td>#${d.no}</td><td>${esc(d.name)}<br><small>${esc(maskEmail(entrants.get(d.id)?.email ?? d.id))}</small></td>
    <td>${statusHu[d.status]}${d.checks ? `<br><small>Automatikus ellenőrzés: ${d.checks.filter((c) => c.ok).length}/${d.checks.length} feltétel teljesül</small>` : ''}${d.reason ? `<br><small>${esc(reasonLabel(d.reason))}${d.note ? ' – ' + esc(d.note) : ''}</small>` : ''}</td></tr>`).join('');
  const winners = PRIZES.map((p) => {
    const w = roundState(s.draws, p.round).winner;
    return `<tr><td>${p.label}</td><td>${esc(p.title)} ${esc(p.extra)}</td><td>${w ? esc(w.name) : '—'}</td></tr>`;
  }).join('');
  const html = `<!doctype html><html lang="hu"><head><meta charset="utf-8"><title>Sorsolási jegyzőkönyv</title><style>
    body{font-family:Arial,Helvetica,sans-serif;font-size:12px;color:#111;margin:28px}h1{font-size:18px;margin:0 0 4px}h2{font-size:14px;margin:22px 0 8px}
    table{border-collapse:collapse;width:100%}td,th{border:1px solid #999;padding:5px 6px;text-align:left;vertical-align:top}th{background:#eee}small{color:#555}
    .meta td{border:none;padding:2px 8px 2px 0}.sig{margin-top:48px;display:flex;gap:60px}.sig div{border-top:1px solid #333;padding-top:4px;width:220px}
  </style></head><body>
  <h1>Dartiqon induló nyereményjáték – sorsolási jegyzőkönyv</h1>
  <table class="meta">
    <tr><td>Sorsolás azonosítója:</td><td>${esc(s.id)}</td></tr>
    <tr><td>Megkezdve:</td><td>${esc(dt(s.createdAt))}</td></tr>
    <tr><td>Lezárva:</td><td>${esc(dt(s.finishedAt))}</td></tr>
    <tr><td>Lebonyolította:</td><td>${esc(s.createdBy)}</td></tr>
    <tr><td>Érvényes nevezők száma a kezdéskor:</td><td>${s.pool.length}</td></tr>
    <tr><td>Nevezői kör ujjlenyomata (SHA-256):</td><td style="word-break:break-all">${esc(s.poolHash)}</td></tr>
    <tr><td>Véletlenszám-generátor:</td><td>böngésző kriptográfiai generátora (crypto.getRandomValues), egyenletes eloszlás elutasításos mintavétellel</td></tr>
  </table>
  <h2>Nyertesek</h2><table><tr><th>Díj</th><th>Nyeremény</th><th>Nyertes</th></tr>${winners}</table>
  <h2>A húzások sorrendben</h2>
  <table><tr><th>#</th><th>Díj</th><th>Időpont</th><th>Nevezők</th><th>Véletlenszám</th><th>Index</th><th>Sorszám</th><th>Kihúzott nevező</th><th>Döntés</th></tr>${rows}</table>
  <p><small>A rendszer minden kihúzott nevezőnél automatikusan ellenőrzi: a szabályzat elfogadása (18+ nyilatkozattal), határidőn belüli nevezés és megerősítés, megerősített e-mail-cím, aktív feliratkozás. A 18. életév betöltésének igazolása a nyertes értesítése után történik. Sorsolás a 3., 2. és 1. díj sorrendjében; előre kisorsolt pótnyertes nincs. A már kihúzott nevező az adott sorsolásban nem húzható újra.</small></p>
  <div class="sig"><div>Szervező képviselője</div><div>Tanú</div></div>
  <script>window.onload=function(){window.print()}</script></body></html>`;
  const w = window.open('', '_blank');
  if (!w) return false;
  w.document.write(html);
  w.document.close();
  return true;
}

/* ---------- oldal ---------- */
export default function GiveawayDrawPage() {
  const toast = useToast();
  const [subs, setSubs] = useState<{ id: string; v: Record<string, unknown> }[] | null>(null);
  const [sessions, setSessions] = useState<Session[] | null>(null);
  const [subsErr, setSubsErr] = useState('');
  const [logErr, setLogErr] = useState('');
  const [mode, setMode] = useState<'test' | 'live'>('test');
  const [testSession, setTestSession] = useState<Session | null>(null);
  const [liveId, setLiveId] = useState<string | null>(null);
  const [confirmStart, setConfirmStart] = useState(false);
  const [confirmFinish, setConfirmFinish] = useState(false);
  const [reject, setReject] = useState<{ draw: Draw; kind: 'rejected' | 'revoked' } | null>(null);
  const [reason, setReason] = useState<Reason>('not_confirmed');
  const [note, setNote] = useState('');
  const [spin, setSpin] = useState<{ strip: number[]; target: number; draw: Draw } | null>(null);
  const [reveal, setReveal] = useState<Draw | null>(null);
  const [busy, setBusy] = useState(false);
  const [full, setFull] = useState(false);
  const stageRef = useRef<HTMLDivElement>(null);

  useEffect(() => onSnapshot(collection(db, 'subscribers'),
    (s) => setSubs(s.docs.map((d) => ({ id: d.id, v: d.data() }))),
    (e) => { setSubsErr(e.message); setSubs([]); }), []);

  useEffect(() => onSnapshot(collection(db, 'giveaway_draws'),
    (s) => { setLogErr(''); setSessions(s.docs.map((d) => sessionFromDoc(d.id, d.data())).sort((a, b) => (b.createdAt?.getTime() ?? 0) - (a.createdAt?.getTime() ?? 0))); },
    (e) => { setLogErr(e.message); setSessions([]); }), []);

  useEffect(() => {
    const f = () => setFull(document.fullscreenElement === stageRef.current);
    document.addEventListener('fullscreenchange', f);
    return () => document.removeEventListener('fullscreenchange', f);
  }, []);

  /** Érvényes nevezők, nevezési sorrendben számozva (ez a sorszám látszik a pörgetőn). */
  const entrants = useMemo(() => {
    const list = (subs ?? []).filter((s) => isEligible(s.v)).map((s) => ({
      id: s.id, name: String(s.v.giveawayName ?? '').trim() || 'Név nélkül', email: String(s.v.email ?? s.id), enteredAt: toDate(s.v.giveawayEnteredAt),
    }));
    list.sort((a, b) => (a.enteredAt?.getTime() ?? 0) - (b.enteredAt?.getTime() ?? 0) || a.id.localeCompare(b.id));
    return list.map((e, i) => ({ ...e, no: i + 1 })) as Entrant[];
  }, [subs]);
  const byId = useMemo(() => new Map(entrants.map((e) => [e.id, e])), [entrants]);
  const digits = Math.max(3, String(entrants.length).length);

  // az utolsó, le nem zárt éles sorsolás automatikusan folytatható (pl. oldalfrissítés után)
  const openLive = useMemo(() => (sessions ?? []).find((s) => !s.test && !s.finishedAt) ?? null, [sessions]);
  const live = useMemo(() => (sessions ?? []).find((s) => s.id === (liveId ?? openLive?.id)) ?? null, [sessions, liveId, openLive]);
  const finishedLive = useMemo(() => (sessions ?? []).filter((s) => !s.test && s.finishedAt), [sessions]);
  const session = mode === 'live' ? live : testSession;

  const draws = session?.draws ?? [];
  const states = PRIZES.map((p) => ({ ...p, ...roundState(draws, p.round) }));
  const current = [...states].reverse().find((s) => !s.winner); // sorrend: 3. → 2. → 1. díj
  const allDone = !current;

  /** A húzható kör: a kezdéskori nevezők közül, akik most is érvényesek, és ebben a sorsolásban még nem húztuk ki őket. */
  const drawable = useMemo(() => {
    if (!session) return [] as Entrant[];
    const used = new Set(session.draws.map((d) => d.id));
    return session.pool.map((id) => byId.get(id)).filter((e): e is Entrant => Boolean(e) && !used.has(e!.id));
  }, [session, byId]);

  const save = useCallback(async (s: Session, nextDraws: Draw[], extra: DocumentData = {}) => {
    if (s.test) { setTestSession({ ...s, draws: nextDraws, ...(extra.finishedAt ? { finishedAt: new Date() } : {}) }); return; }
    await updateDoc(doc(db, 'giveaway_draws', s.id), { draws: nextDraws, updatedAt: serverTimestamp(), ...extra });
  }, []);

  const startSession = async (test: boolean) => {
    const pool = entrants.map((e) => e.id);
    const poolHash = await sha256(pool.join('\n'));
    const by = auth.currentUser?.email ?? auth.currentUser?.uid ?? '';
    if (test) {
      setTestSession({ id: `proba-${Date.now()}`, test: true, createdAt: new Date(), createdBy: by, pool, poolHash, draws: [], finishedAt: null });
      setReveal(null);
      return;
    }
    const ref = await addDoc(collection(db, 'giveaway_draws'), { test: false, createdAt: serverTimestamp(), createdBy: by, pool, poolHash, draws: [], finishedAt: null });
    setLiveId(ref.id);
    setReveal(null);
    toast('Az éles sorsolás elindult, a nevezői kör rögzítve.');
  };

  const doDraw = () => {
    if (!session || !current || drawable.length === 0 || spin) return;
    const { index, rand } = fairIndex(drawable.length);
    const winner = drawable[index];
    const d: Draw = { round: current.round, at: new Date().toISOString(), rand, index, poolSize: drawable.length, id: winner.id, no: winner.no, name: winner.name, status: 'pending' };
    // pörgető-szalag: véletlen sorszámok, a végén (a középső sorban) a kihúzott
    const len = 46;
    const nums = drawable.map((e) => e.no);
    const strip = Array.from({ length: len }, () => nums[Math.floor(Math.random() * nums.length)]);
    const target = len - 3;
    strip[target] = winner.no;
    setReveal(null);
    setSpin({ strip, target, draw: d });
  };

  const onSpinDone = async () => {
    if (!spin || !session) return;
    // automatikus ellenőrzés a nevező aktuális adatain; megfelelés esetén azonnal nyertes, különben új húzás kell
    const checks = checkEntry(subs?.find((x) => x.id === spin.draw.id)?.v);
    const failed = checks.find((c) => !c.ok);
    const d: Draw = { ...spin.draw, checks, status: failed ? 'rejected' : 'winner', decidedAt: new Date().toISOString(), ...(failed ? { reason: failed.key } : {}) };
    setSpin(null);
    setReveal(d);
    try {
      await save(session, [...session.draws, d]);
      if (failed) toast(`Nem felel meg (${failed.label.toLowerCase()}) – új húzás következik.`, 'gold');
    } catch (e) { toast(e instanceof Error ? e.message : 'A mentés nem sikerült.', 'red'); }
  };

  const decide = async (d: Draw, status: DrawStatus, r?: Reason, n?: string) => {
    if (!session) return;
    setBusy(true);
    const next = session.draws.map((x) => (x.at === d.at && x.id === d.id ? { ...x, status, decidedAt: new Date().toISOString(), ...(r ? { reason: r } : {}), ...(n ? { note: n } : {}) } : x));
    try {
      await save(session, next);
      if (status === 'winner') toast(`${d.name} – ${d.round}. díj nyertese.`);
      else { toast(`Új húzás következik: ${d.round}. díj.`, 'gold'); setReveal(null); }
    } catch (e) { toast(e instanceof Error ? e.message : 'A mentés nem sikerült.', 'red'); } finally { setBusy(false); }
  };

  const finish = async () => {
    if (!session) return;
    try { await save(session, session.draws, { finishedAt: serverTimestamp() }); toast('A sorsolás lezárva.'); } catch (e) { toast(e instanceof Error ? e.message : 'A lezárás nem sikerült.', 'red'); }
  };

  const toggleFull = () => {
    if (document.fullscreenElement) void document.exitFullscreen();
    else void stageRef.current?.requestFullscreen?.();
  };

  const pending = draws.some((x) => x.status === 'pending');
  const shownReveal = reveal ? session?.draws.find((x) => x.at === reveal.at && x.id === reveal.id) ?? reveal : null;
  const prizeOf = (round: number) => PRIZES.find((p) => p.round === round)!;

  if (subs === null || sessions === null) return <Spinner />;

  return (
    <div>
      <style>{`
        @keyframes gd-fall { 0% { transform: translateY(0) rotate(0deg); opacity: 1 } 100% { transform: translateY(110vh) rotate(720deg); opacity: .9 } }
        .gd-confetti { animation-name: gd-fall; animation-timing-function: cubic-bezier(.3,.6,.5,1); animation-fill-mode: forwards }
        @keyframes gd-pop { 0% { transform: scale(.85); opacity: 0 } 60% { transform: scale(1.04); opacity: 1 } 100% { transform: scale(1) } }
        .gd-pop { animation: gd-pop .55s ease-out both }
        @keyframes gd-glow { 0%,100% { box-shadow: 0 0 0 rgba(217,165,20,0) } 50% { box-shadow: 0 0 48px rgba(217,165,20,.45) } }
        .gd-glow { animation: gd-glow 2.4s ease-in-out infinite }
      `}</style>

      <PageHeader
        title="Sorsolás"
        sub="Induló nyereményjáték — 3 kör az érvényes nevezők között, a 3., 2. és 1. díj sorrendjében."
        right={<Segmented value={mode} onChange={(v) => { setMode(v); setReveal(null); }} options={[{ value: 'test', label: 'Próbasorsolás' }, { value: 'live', label: 'Éles sorsolás' }]} />}
      />

      {subsErr && <div className="mb-4"><Notice tone="red">A nevezők nem tölthetők be: {subsErr}</Notice></div>}
      {mode === 'live' && logErr && (
        <div className="mb-4"><Notice tone="red">A sorsolási napló nem érhető el ({logErr}). Az éles sorsoláshoz telepíteni kell a `giveaway_draws` Firestore-szabályt; addig a próbasorsolás használható.</Notice></div>
      )}

      <div className="grid grid-cols-2 xl:grid-cols-4 gap-3 sm:gap-4 mb-6">
        <StatCard icon="Users" label="Érvényes nevezők" value={fmtInt(entrants.length)} />
        <StatCard icon="Sparkle" tone="cyan" label="Ebben a körben húzható" value={session ? fmtInt(drawable.length) : '—'} />
        <StatCard icon="Trophy" tone="gold" label="Kisorsolt díjak" value={`${states.filter((s) => s.winner).length} / 3`} />
        <StatCard icon="ArrowsClockwise" tone="purple" label="Húzások összesen" value={fmtInt(draws.length)} />
      </div>

      {mode === 'test' && <div className="mb-4"><Notice tone="cyan">Próbasorsolás: semmi nem mentődik, bármikor újrakezdhető. Az élő adás előtti főpróbához.</Notice></div>}

      {/* ---------- színpad ---------- */}
      <div ref={stageRef} className={`relative overflow-hidden rounded-2xl border border-[#31383D] ${full ? 'bg-[#010306] flex flex-col justify-center p-10' : 'bg-[#15191E] p-5 sm:p-8'} mb-6`}>
        {shownReveal?.status === 'winner' && <Confetti key={shownReveal.at} />}
        <div className="flex items-start justify-between gap-3 mb-6">
          <div className="flex items-center gap-3">
            <img src={logo} alt="Dartiqon" className="h-8" />
            {mode === 'test' && <Badge tone="gold">PRÓBA</Badge>}
          </div>
          <Button kind="ghost" icon="ArrowsOutCardinal" onClick={toggleFull}>{full ? 'Kilépés' : 'Teljes képernyő'}</Button>
        </div>

        {/* díjsáv */}
        <div className="grid grid-cols-3 gap-3 mb-8">
          {states.map((s) => {
            const active = current?.round === s.round && !!session;
            return (
              <div key={s.round} className={`rounded-xl border p-3 sm:p-4 transition ${s.winner ? 'border-[#2bc48a]/60 bg-[#2bc48a]/10' : active ? 'border-[#d9a514] bg-[#d9a514]/10 gd-glow' : 'border-[#31383D] bg-[#0b0f14]'}`}>
                <div className="flex items-center gap-2 text-[12px] font-bold uppercase tracking-wide" style={{ color: s.winner ? '#2bc48a' : active ? '#d9a514' : '#8a8f99' }}>
                  <Icon name={s.winner ? 'CheckCircle' : 'Trophy'} size={16} />{s.label}
                </div>
                <div className="mt-1.5 text-white text-[13px] sm:text-[14px] font-semibold leading-snug">{s.title}</div>
                <div className="text-[#B8B8C3] text-[11.5px] sm:text-[12px] leading-snug">{s.extra}</div>
                {s.winner && <div className="mt-2 text-white font-extrabold italic text-[15px] truncate">{s.winner.name}</div>}
              </div>
            );
          })}
        </div>

        {!session ? (
          <div className="text-center py-10">
            <div className="text-[13px] uppercase tracking-[0.2em] text-[#8a8f99]">{entrants.length} érvényes nevező</div>
            <div className="mt-2 text-white text-[26px] font-extrabold italic uppercase">A sorsolás még nem indult el</div>
            <div className="mt-6">
              {mode === 'test'
                ? <Button icon="PlayCircle" disabled={!entrants.length} onClick={() => void startSession(true)}>Próbasorsolás indítása</Button>
                : <Button icon="PlayCircle" disabled={!entrants.length || !!logErr} onClick={() => setConfirmStart(true)}>Éles sorsolás indítása</Button>}
            </div>
          </div>
        ) : (
          <div className="grid lg:grid-cols-[1fr_1fr] gap-8 items-center">
            <div>
              <div className="text-center mb-3 text-[13px] uppercase tracking-[0.2em] text-[#8a8f99]">
                {current ? `${current.label} · ${fmtInt(drawable.length)} nevező` : 'Mindhárom díj kisorsolva'}
              </div>
              <Reel strip={spin?.strip ?? [0, 0, shownReveal?.no ?? 0, 0, 0]}
                target={spin?.target ?? 2} spinning={!!spin} digits={digits} onDone={() => void onSpinDone()} />
              <div className="mt-5 flex justify-center">
                <Button icon="Sparkle" className="!h-[48px] !px-8 !text-[16px]" disabled={!current || !!spin || !!pending || drawable.length === 0 || (mode === 'live' && !!session.finishedAt)} onClick={doDraw}>
                  {current ? `Sorsolás – ${current.label}` : 'Kész'}
                </Button>
              </div>
              {current && drawable.length === 0 && <div className="mt-3 text-center text-[13px] text-[#ef4444]">Nincs több húzható nevező.</div>}
            </div>

            <div className="min-h-[300px] flex items-center justify-center">
              {spin ? (
                <div className="text-center text-[#8a8f99] text-[15px] uppercase tracking-[0.25em]">Sorsolás folyamatban…</div>
              ) : shownReveal ? (
                <div key={shownReveal.at} className="gd-pop w-full text-center rounded-2xl border border-[#d9a514]/60 bg-[#d9a514]/[0.07] px-6 py-8">
                  <div className="text-[12px] font-bold uppercase tracking-[0.25em] text-[#d9a514]">{prizeOf(shownReveal.round).label} · #{String(shownReveal.no).padStart(digits, '0')}</div>
                  <div className="mt-3 text-white font-extrabold italic uppercase leading-tight break-words" style={{ fontSize: full ? 56 : 38 }}>{shownReveal.name}</div>
                  <div className="mt-2 text-[#B8B8C3] text-[14px]">{maskEmail(byId.get(shownReveal.id)?.email ?? shownReveal.id)}</div>
                  <div className="mt-4">
                    {shownReveal.status === 'winner' && <Badge tone="green">Nyertes</Badge>}
                    {shownReveal.status === 'rejected' && <Badge tone="red">Nem felel meg – új húzás</Badge>}
                    {shownReveal.status === 'revoked' && <Badge tone="red">Visszavonva</Badge>}
                  </div>
                  {shownReveal.checks && (
                    <div className="mt-5 mx-auto max-w-[360px] flex flex-col gap-1.5 text-left">
                      {shownReveal.checks.map((c) => (
                        <div key={c.key} className="flex items-center gap-2 text-[13px]" style={{ color: c.ok ? '#2bc48a' : '#ef4444' }}>
                          <Icon name={c.ok ? 'CheckCircle' : 'X'} size={16} />
                          <span className={c.ok ? 'text-[#B8B8C3]' : ''}>{c.label}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center text-[#8a8f99] text-[15px]">{current ? 'Nyomd meg a sorsolás gombot.' : 'A sorsolás lezárható.'}</div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* ---------- napló és műveletek ---------- */}
      {session && (
        <Card className="mb-6">
          <SectionTitle right={
            <div className="flex flex-wrap gap-2">
              <Button kind="ghost" icon="FileText" disabled={!draws.length} onClick={() => { if (!printProtocol(session, byId)) toast('A felugró ablakot engedélyezni kell.', 'red'); }}>Jegyzőkönyv</Button>
              {mode === 'test'
                ? <Button kind="ghost" icon="ArrowCounterClockwise" onClick={() => { setTestSession(null); setReveal(null); }}>Próba újrakezdése</Button>
                : !session.finishedAt && <Button kind="ghost" icon="Lock" disabled={!allDone} onClick={() => setConfirmFinish(true)}>Sorsolás lezárása</Button>}
            </div>
          }>Húzások</SectionTitle>
          {mode === 'live' && (
            <div className="mb-4 text-[12px] text-[#8a8f99] break-all">
              Azonosító: {session.id} · indítva: {dt(session.createdAt)} · nevezői kör: {session.pool.length} fő · ujjlenyomat: {session.poolHash}
              {session.finishedAt && <> · lezárva: {dt(session.finishedAt)}</>}
            </div>
          )}
          {draws.length === 0 ? <div className="text-[13px] text-[#8a8f99]">Még nincs húzás.</div> : (
            <div className="overflow-x-auto">
              <table className="w-full text-[13px]">
                <thead>
                  <tr className="text-left text-[#8a8f99] text-[12px] border-b border-[#31383D]">
                    <th className="py-2 pr-3 font-medium">Díj</th>
                    <th className="py-2 pr-3 font-medium">Időpont</th>
                    <th className="py-2 pr-3 font-medium">Sorszám</th>
                    <th className="py-2 pr-3 font-medium">Név</th>
                    <th className="py-2 pr-3 font-medium">Állapot</th>
                    <th className="py-2 font-medium" />
                  </tr>
                </thead>
                <tbody>
                  {draws.map((d) => (
                    <tr key={d.at + d.id} className="border-b border-[#31383D]/60">
                      <td className="py-2.5 pr-3 text-white whitespace-nowrap">{d.round}. díj</td>
                      <td className="py-2.5 pr-3 text-[#B8B8C3] whitespace-nowrap">{dt(d.at)}</td>
                      <td className="py-2.5 pr-3 text-[#B8B8C3] tabular-nums">#{String(d.no).padStart(digits, '0')}</td>
                      <td className="py-2.5 pr-3 text-white">{d.name}<div className="text-[11.5px] text-[#8a8f99]">{byId.get(d.id)?.email ?? d.id}</div></td>
                      <td className="py-2.5 pr-3">
                        <Badge tone={d.status === 'winner' ? 'green' : d.status === 'pending' ? 'gold' : 'red'}>
                          {{ winner: 'Nyertes', pending: 'Döntésre vár', rejected: 'Nem felelt meg', revoked: 'Visszavonva' }[d.status]}
                        </Badge>
                        {d.reason && <div className="mt-1 text-[11.5px] text-[#8a8f99]">{reasonLabel(d.reason)}{d.note ? ` – ${d.note}` : ''}</div>}
                      </td>
                      <td className="py-2.5 text-right whitespace-nowrap">
                        {d.status === 'winner' && !session.finishedAt && (
                          <Button kind="ghost" icon="UserMinus" onClick={() => { setReason('no_reply'); setNote(''); setReject({ draw: d, kind: 'revoked' }); }}>Visszavonás</Button>
                        )}
                        {d.status === 'pending' && !spin && (
                          <Button kind="ghost" icon="Eye" onClick={() => setReveal(d)}>Megnyitás</Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}

      {mode === 'live' && finishedLive.length > 0 && (
        <Card>
          <SectionTitle>Lezárt sorsolások</SectionTitle>
          <div className="flex flex-col gap-2">
            {finishedLive.map((s) => (
              <div key={s.id} className="flex flex-wrap items-center justify-between gap-3 py-2 border-b border-[#31383D]/60 last:border-0">
                <div className="text-[13px]">
                  <div className="text-white">{dt(s.createdAt)} · {s.pool.length} nevező</div>
                  <div className="text-[#8a8f99] text-[12px]">{PRIZES.map((p) => `${p.label}: ${roundState(s.draws, p.round).winner?.name ?? '—'}`).join(' · ')}</div>
                </div>
                <Button kind="ghost" icon="FileText" onClick={() => { if (!printProtocol(s, byId)) toast('A felugró ablakot engedélyezni kell.', 'red'); }}>Jegyzőkönyv</Button>
              </div>
            ))}
          </div>
        </Card>
      )}

      {confirmStart && (
        <ConfirmModal
          title="Éles sorsolás indítása"
          text={<>A sorsolás most rögzíti a {fmtInt(entrants.length)} érvényes nevezőt, és minden húzás a jegyzőkönyvbe kerül. A szabályzat szerint a sorsolás 2026. november 8-án 20:00-kor van. Indítod?</>}
          confirmLabel="Indítás"
          onConfirm={async () => { try { await startSession(false); } catch (e) { toast(e instanceof Error ? e.message : 'Az indítás nem sikerült.', 'red'); } setConfirmStart(false); }}
          onClose={() => setConfirmStart(false)}
        />
      )}

      {confirmFinish && (
        <ConfirmModal
          title="Sorsolás lezárása"
          text="Lezárás után a húzások nem módosíthatók. Ha egy nyertes később nem jogosult, új sorsolást kell indítani az adott díjra."
          confirmLabel="Lezárás"
          onConfirm={async () => { await finish(); setConfirmFinish(false); }}
          onClose={() => setConfirmFinish(false)}
        />
      )}

      {reject && (
        <Modal
          title={reject.kind === 'revoked' ? 'Nyertes visszavonása' : 'A kihúzott nevező nem felel meg'}
          onClose={() => setReject(null)}
          width={480}
          footer={<>
            <Button kind="ghost" onClick={() => setReject(null)}>Mégse</Button>
            <Button kind="danger" disabled={busy || (reason === 'other' && !note.trim())} onClick={async () => { await decide(reject.draw, reject.kind, reason, note.trim()); setReject(null); }}>
              {reject.kind === 'revoked' ? 'Visszavonás, új húzás' : 'Új húzás erre a díjra'}
            </Button>
          </>}
        >
          <p className="text-[14px] text-[#B8B8C3] mb-4"><b className="text-white">{reject.draw.name}</b> · {reject.draw.round}. díj. Az ok a jegyzőkönyvbe kerül, a nevező ebben a sorsolásban nem húzható újra.</p>
          <Field label="Ok"><Select value={reason} onChange={setReason} options={REASONS.map((r) => ({ value: r.value, label: r.label }))} /></Field>
          <Field label="Megjegyzés" className="mt-3"><TextArea value={note} onChange={(e) => setNote(e.target.value)} placeholder={reason === 'other' ? 'Kötelező' : 'Nem kötelező'} /></Field>
        </Modal>
      )}
    </div>
  );
}
